<template>
  <div class="breach-container">
    <div class="breach-decor">
      <div class="decor-orb orb-1"></div>
      <div class="decor-orb orb-2"></div>
      <div class="decor-orb orb-3"></div>
      <div class="decor-line line-1"></div>
      <div class="decor-line line-2"></div>
      <div class="decor-line line-3"></div>
    </div>

    <!-- ====== HERO ====== -->
    <section class="hero">
      <div class="hero-content">
        <div class="hero-card">
          <div class="section-badge">{{ localize('BREACH_BADGE') }}</div>
          <h1 class="hero-title">{{ localize('BREACH_TITLE') }}</h1>
          <p class="hero-subtitle">{{ localize('BREACH_SUBTITLE') }}</p>
        </div>
      </div>
      <div class="hero-divider"></div>
    </section>

    <!-- ====== STATS BANNER ====== -->
    <section class="stats-section">
      <div class="stats-inner">
        <div class="stats-grid">
          <div class="stat-glass" v-for="(stat, idx) in stats" :key="idx">
            <div class="stat-icon" v-html="getRawIcon(stat.icon)"></div>
            <div class="stat-value">{{ localize(stat.valueKey) }}</div>
            <div class="stat-label">{{ localize(stat.labelKey) }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ====== BREACH CASE STUDIES ====== -->
    <section class="cases-section">
      <div class="cases-inner">
        <div class="section-header">
          <div class="section-badge">{{ localize('BREACH_CASES_BADGE') }}</div>
          <h2 class="section-title">{{ localize('BREACH_CASES_TITLE') }}</h2>
          <p class="section-desc">{{ localize('BREACH_CASES_DESC') }}</p>
        </div>

        <!-- Japan Breaches -->
        <div class="region-label">
          <span class="region-icon" v-html="getRawIcon('ICON_GLOBE')"></span>
          <span>{{ localize('BREACH_REGION_JAPAN') }}</span>
        </div>

        <div class="breach-card" v-for="(breach, idx) in breachesJapan" :key="'jp-' + idx">
          <div class="breach-severity" :class="'severity-' + breach.severity"></div>
          <div class="breach-body">
            <div class="breach-header">
              <div class="breach-title-row">
                <span class="breach-icon" v-html="getRawIcon(breach.icon)"></span>
                <h3 class="breach-name">{{ localize(breach.nameKey) }}</h3>
              </div>
              <div class="breach-meta">
                <span class="breach-tag tag-date">{{ localize(breach.dateKey) }}</span>
                <span class="breach-tag tag-sector">{{ localize(breach.sectorKey) }}</span>
                <span class="breach-tag tag-records">{{ localize(breach.recordsKey) }}</span>
              </div>
            </div>
            <div class="breach-details">
              <div class="breach-detail-block">
                <div class="breach-detail-label">{{ localize('BREACH_WHAT_HAPPENED') }}</div>
                <p class="breach-detail-text">{{ localize(breach.whatKey) }}</p>
              </div>
              <div class="breach-detail-block">
                <div class="breach-detail-label">{{ localize('BREACH_VENDOR_ANGLE') }}</div>
                <p class="breach-detail-text breach-vendor-text">{{ localize(breach.vendorKey) }}</p>
              </div>
              <div class="breach-detail-block">
                <div class="breach-detail-label">{{ localize('BREACH_ENBARR_PREVENTS') }}</div>
                <p class="breach-detail-text breach-solution-text">{{ localize(breach.preventKey) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Global Breaches -->
        <div class="region-label region-label-global">
          <span class="region-icon" v-html="getRawIcon('ICON_NETWORK')"></span>
          <span>{{ localize('BREACH_REGION_GLOBAL') }}</span>
        </div>

        <div class="breach-card" v-for="(breach, idx) in breachesGlobal" :key="'gl-' + idx">
          <div class="breach-severity" :class="'severity-' + breach.severity"></div>
          <div class="breach-body">
            <div class="breach-header">
              <div class="breach-title-row">
                <span class="breach-icon" v-html="getRawIcon(breach.icon)"></span>
                <h3 class="breach-name">{{ localize(breach.nameKey) }}</h3>
              </div>
              <div class="breach-meta">
                <span class="breach-tag tag-date">{{ localize(breach.dateKey) }}</span>
                <span class="breach-tag tag-sector">{{ localize(breach.sectorKey) }}</span>
                <span class="breach-tag tag-records">{{ localize(breach.recordsKey) }}</span>
              </div>
            </div>
            <div class="breach-details">
              <div class="breach-detail-block">
                <div class="breach-detail-label">{{ localize('BREACH_WHAT_HAPPENED') }}</div>
                <p class="breach-detail-text">{{ localize(breach.whatKey) }}</p>
              </div>
              <div class="breach-detail-block">
                <div class="breach-detail-label">{{ localize('BREACH_VENDOR_ANGLE') }}</div>
                <p class="breach-detail-text breach-vendor-text">{{ localize(breach.vendorKey) }}</p>
              </div>
              <div class="breach-detail-block">
                <div class="breach-detail-label">{{ localize('BREACH_ENBARR_PREVENTS') }}</div>
                <p class="breach-detail-text breach-solution-text">{{ localize(breach.preventKey) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ====== THE PATTERN ====== -->
    <section class="pattern-section">
      <div class="pattern-inner">
        <div class="section-header">
          <div class="section-badge">{{ localize('BREACH_PATTERN_BADGE') }}</div>
          <h2 class="section-title">{{ localize('BREACH_PATTERN_TITLE') }}</h2>
          <p class="section-desc">{{ localize('BREACH_PATTERN_DESC') }}</p>
        </div>

        <div class="pattern-flow">
          <div class="pattern-node" v-for="(node, idx) in patternNodes" :key="'p-' + idx">
            <div class="pattern-node-icon" v-html="getRawIcon(node.icon)"></div>
            <div class="pattern-node-label">{{ localize(node.labelKey) }}</div>
            <div class="pattern-node-desc">{{ localize(node.descKey) }}</div>
            <div class="pattern-connector" v-if="idx < patternNodes.length - 1">
              <div class="connector-line"></div>
              <div class="connector-arrow"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ====== HOW ENBARR PREVENTS ====== -->
    <section class="solution-section">
      <div class="solution-inner">
        <div class="section-header">
          <div class="section-badge">{{ localize('BREACH_SOLUTION_BADGE') }}</div>
          <h2 class="section-title">{{ localize('BREACH_SOLUTION_TITLE') }}</h2>
          <p class="section-desc">{{ localize('BREACH_SOLUTION_DESC') }}</p>
        </div>

        <div class="solution-grid">
          <div class="solution-card" v-for="(sol, idx) in solutions" :key="'s-' + idx">
            <div class="solution-card-icon" v-html="getRawIcon(sol.icon)"></div>
            <h3 class="solution-card-title">{{ localize(sol.titleKey) }}</h3>
            <p class="solution-card-text">{{ localize(sol.descKey) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ====== CTA ====== -->
    <section class="cta-section">
      <div class="cta-inner">
        <div class="cta-card">
          <div class="cta-accent"></div>
          <h2 class="cta-title">{{ localize('BREACH_CTA_TITLE') }}</h2>
          <p class="cta-text">{{ localize('BREACH_CTA_TEXT') }}</p>
          <a href="/technology" class="cta-button">
            <span>{{ localize('BREACH_CTA_BUTTON') }}</span>
            <span class="cta-arrow">&rarr;</span>
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>

import {translationStore} from "@/stores/translation";
import {iconStore} from "@/stores/icon";

export default {
    "name": "Breaches",
    setup () {

        const translation = translationStore(),
            icon = iconStore(),
            getRawIcon = function (i) {

                return icon.get(i);

            },

            stats = [
                {"icon": "ICON_LINK", "valueKey": "BREACH_STAT_1_VALUE", "labelKey": "BREACH_STAT_1_LABEL"},
                {"icon": "ICON_ALERT", "valueKey": "BREACH_STAT_2_VALUE", "labelKey": "BREACH_STAT_2_LABEL"},
                {"icon": "ICON_ACTIVITY", "valueKey": "BREACH_STAT_3_VALUE", "labelKey": "BREACH_STAT_3_LABEL"},
                {"icon": "ICON_LAYERS", "valueKey": "BREACH_STAT_4_VALUE", "labelKey": "BREACH_STAT_4_LABEL"}
            ],

            breachesJapan = [
                {
                    "severity": "critical",
                    "icon": "ICON_BUILDING",
                    "nameKey": "BREACH_JP_HOSPITAL_NAME",
                    "dateKey": "BREACH_JP_HOSPITAL_DATE",
                    "sectorKey": "BREACH_JP_HOSPITAL_SECTOR",
                    "recordsKey": "BREACH_JP_HOSPITAL_RECORDS",
                    "whatKey": "BREACH_JP_HOSPITAL_WHAT",
                    "vendorKey": "BREACH_JP_HOSPITAL_VENDOR",
                    "preventKey": "BREACH_JP_HOSPITAL_PREVENT"
                },
                {
                    "severity": "critical",
                    "icon": "ICON_LINK",
                    "nameKey": "BREACH_JP_TOYOTA_NAME",
                    "dateKey": "BREACH_JP_TOYOTA_DATE",
                    "sectorKey": "BREACH_JP_TOYOTA_SECTOR",
                    "recordsKey": "BREACH_JP_TOYOTA_RECORDS",
                    "whatKey": "BREACH_JP_TOYOTA_WHAT",
                    "vendorKey": "BREACH_JP_TOYOTA_VENDOR",
                    "preventKey": "BREACH_JP_TOYOTA_PREVENT"
                },
                {
                    "severity": "high",
                    "icon": "ICON_GLOBE",
                    "nameKey": "BREACH_JP_JAXA_NAME",
                    "dateKey": "BREACH_JP_JAXA_DATE",
                    "sectorKey": "BREACH_JP_JAXA_SECTOR",
                    "recordsKey": "BREACH_JP_JAXA_RECORDS",
                    "whatKey": "BREACH_JP_JAXA_WHAT",
                    "vendorKey": "BREACH_JP_JAXA_VENDOR",
                    "preventKey": "BREACH_JP_JAXA_PREVENT"
                },
                {
                    "severity": "high",
                    "icon": "ICON_DATABASE",
                    "nameKey": "BREACH_JP_CAPCOM_NAME",
                    "dateKey": "BREACH_JP_CAPCOM_DATE",
                    "sectorKey": "BREACH_JP_CAPCOM_SECTOR",
                    "recordsKey": "BREACH_JP_CAPCOM_RECORDS",
                    "whatKey": "BREACH_JP_CAPCOM_WHAT",
                    "vendorKey": "BREACH_JP_CAPCOM_VENDOR",
                    "preventKey": "BREACH_JP_CAPCOM_PREVENT"
                },
                {
                    "severity": "high",
                    "icon": "ICON_NETWORK",
                    "nameKey": "BREACH_JP_NTT_NAME",
                    "dateKey": "BREACH_JP_NTT_DATE",
                    "sectorKey": "BREACH_JP_NTT_SECTOR",
                    "recordsKey": "BREACH_JP_NTT_RECORDS",
                    "whatKey": "BREACH_JP_NTT_WHAT",
                    "vendorKey": "BREACH_JP_NTT_VENDOR",
                    "preventKey": "BREACH_JP_NTT_PREVENT"
                }
            ],

            breachesGlobal = [
                {
                    "severity": "critical",
                    "icon": "ICON_LAYERS",
                    "nameKey": "BREACH_GL_SOLARWINDS_NAME",
                    "dateKey": "BREACH_GL_SOLARWINDS_DATE",
                    "sectorKey": "BREACH_GL_SOLARWINDS_SECTOR",
                    "recordsKey": "BREACH_GL_SOLARWINDS_RECORDS",
                    "whatKey": "BREACH_GL_SOLARWINDS_WHAT",
                    "vendorKey": "BREACH_GL_SOLARWINDS_VENDOR",
                    "preventKey": "BREACH_GL_SOLARWINDS_PREVENT"
                },
                {
                    "severity": "critical",
                    "icon": "ICON_DATABASE",
                    "nameKey": "BREACH_GL_MOVEIT_NAME",
                    "dateKey": "BREACH_GL_MOVEIT_DATE",
                    "sectorKey": "BREACH_GL_MOVEIT_SECTOR",
                    "recordsKey": "BREACH_GL_MOVEIT_RECORDS",
                    "whatKey": "BREACH_GL_MOVEIT_WHAT",
                    "vendorKey": "BREACH_GL_MOVEIT_VENDOR",
                    "preventKey": "BREACH_GL_MOVEIT_PREVENT"
                },
                {
                    "severity": "high",
                    "icon": "ICON_LINK",
                    "nameKey": "BREACH_GL_KASEYA_NAME",
                    "dateKey": "BREACH_GL_KASEYA_DATE",
                    "sectorKey": "BREACH_GL_KASEYA_SECTOR",
                    "recordsKey": "BREACH_GL_KASEYA_RECORDS",
                    "whatKey": "BREACH_GL_KASEYA_WHAT",
                    "vendorKey": "BREACH_GL_KASEYA_VENDOR",
                    "preventKey": "BREACH_GL_KASEYA_PREVENT"
                },
                {
                    "severity": "high",
                    "icon": "ICON_KEY",
                    "nameKey": "BREACH_GL_OKTA_NAME",
                    "dateKey": "BREACH_GL_OKTA_DATE",
                    "sectorKey": "BREACH_GL_OKTA_SECTOR",
                    "recordsKey": "BREACH_GL_OKTA_RECORDS",
                    "whatKey": "BREACH_GL_OKTA_WHAT",
                    "vendorKey": "BREACH_GL_OKTA_VENDOR",
                    "preventKey": "BREACH_GL_OKTA_PREVENT"
                },
                {
                    "severity": "critical",
                    "icon": "ICON_ALERT",
                    "nameKey": "BREACH_GL_CHANGE_NAME",
                    "dateKey": "BREACH_GL_CHANGE_DATE",
                    "sectorKey": "BREACH_GL_CHANGE_SECTOR",
                    "recordsKey": "BREACH_GL_CHANGE_RECORDS",
                    "whatKey": "BREACH_GL_CHANGE_WHAT",
                    "vendorKey": "BREACH_GL_CHANGE_VENDOR",
                    "preventKey": "BREACH_GL_CHANGE_PREVENT"
                }
            ],

            patternNodes = [
                {"icon": "ICON_USERS", "labelKey": "BREACH_PATTERN_1_LABEL", "descKey": "BREACH_PATTERN_1_DESC"},
                {"icon": "ICON_KEY", "labelKey": "BREACH_PATTERN_2_LABEL", "descKey": "BREACH_PATTERN_2_DESC"},
                {"icon": "ICON_LAYERS", "labelKey": "BREACH_PATTERN_3_LABEL", "descKey": "BREACH_PATTERN_3_DESC"},
                {"icon": "ICON_BOLT", "labelKey": "BREACH_PATTERN_4_LABEL", "descKey": "BREACH_PATTERN_4_DESC"}
            ],

            solutions = [
                {"icon": "ICON_SHIELD", "titleKey": "BREACH_SOL_1_TITLE", "descKey": "BREACH_SOL_1_DESC"},
                {"icon": "ICON_LOCK", "titleKey": "BREACH_SOL_2_TITLE", "descKey": "BREACH_SOL_2_DESC"},
                {"icon": "ICON_CROSSHAIR", "titleKey": "BREACH_SOL_3_TITLE", "descKey": "BREACH_SOL_3_DESC"},
                {"icon": "ICON_EYE_OFF", "titleKey": "BREACH_SOL_4_TITLE", "descKey": "BREACH_SOL_4_DESC"},
                {"icon": "ICON_NETWORK", "titleKey": "BREACH_SOL_5_TITLE", "descKey": "BREACH_SOL_5_DESC"}
            ];

        return {
            "localize": translation.localize,
            getRawIcon,
            stats,
            breachesJapan,
            breachesGlobal,
            patternNodes,
            solutions
        };

    }
};

</script>
<style scoped>
/* ============================================
   BREACH CONTAINER - Purple/Cyan/Magenta Palette
   Uses global vars from App.vue
   ============================================ */
.breach-container {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: var(--bg-primary);
  font-family: var(--font-body);
  color: var(--text-primary);
}

/* ============================================
   DECORATIVE ORBS & LINES - Vivid purple/cyan
   ============================================ */
.breach-decor {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
.decor-orb {
  position: absolute;
  border-radius: 50%;
  opacity: 0.12;
}
.orb-1 {
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, rgba(124, 58, 237, 0.25), rgba(236, 72, 153, 0.1) 50%, transparent 70%);
  top: -200px;
  right: -150px;
  animation: drift-1 28s ease-in-out infinite;
}
.orb-2 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.2), rgba(124, 58, 237, 0.08) 50%, transparent 70%);
  bottom: 15%;
  left: -120px;
  animation: drift-2 32s ease-in-out infinite;
}
.orb-3 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(236, 72, 153, 0.15), transparent 60%);
  top: 50%;
  right: -80px;
  animation: drift-3 35s ease-in-out infinite;
}
.decor-line {
  position: absolute;
  background: linear-gradient(180deg, transparent, rgba(124, 58, 237, 0.15), transparent);
  opacity: 0.4;
}
.line-1 { width: 1px; height: 60%; top: 5%; left: 12%; }
.line-2 { width: 1px; height: 50%; top: 25%; right: 18%; }
.line-3 { width: 1px; height: 40%; top: 35%; left: 50%; }

@keyframes drift-1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-50px, 70px); }
}
@keyframes drift-2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(60px, -50px); }
}
@keyframes drift-3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-30px, -60px); }
}

/* ============================================
   SHARED ELEMENTS
   ============================================ */
.section-badge {
  display: inline-block;
  padding: 10px 24px;
  margin-bottom: 28px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 3.5px;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #6366f1, #06b6d4);
  border: none;
  border-radius: 30px;
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-family: var(--font-heading);
  font-size: 2.4em;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 22px;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-desc {
  font-size: 1em;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0 auto;
  max-width: 640px;
}

/* ============================================
   HERO
   ============================================ */
.hero {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 100px 20px 60px;
  background: transparent;
}

.hero-content {
  max-width: 840px;
  width: 100%;
}

.hero-card {
  background: var(--bg-card);
  border: 1px solid rgba(124, 58, 237, 0.08);
  border-left: 4px solid transparent;
  border-radius: 2px;
  padding: 64px 52px;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.28), 0 10px 40px rgba(124, 58, 237, 0.16);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

.hero-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #7c3aed, #06b6d4);
}

.hero-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}

.hero-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.36), 0 16px 56px rgba(124, 58, 237, 0.24);
  transform: translateY(-3px);
}

.hero-title {
  font-family: var(--font-heading);
  font-size: 3.2em;
  font-weight: 800;
  line-height: 1.1;
  margin: 0 0 26px;
  letter-spacing: -1.5px;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.1em;
  line-height: 1.85;
  color: var(--text-secondary);
  margin: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.hero-divider {
  width: min(80%, 600px);
  height: 2px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  margin-top: 48px;
  border-radius: 0;
}

/* ============================================
   STATS BANNER
   ============================================ */
.stats-section {
  position: relative;
  z-index: 1;
  padding: 56px 20px 72px;
  background: transparent;
}

.stats-inner {
  max-width: 1100px;
  margin: 0 auto;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 36px;
}

.stat-glass {
  background: var(--bg-card);
  border: 1px solid rgba(124, 58, 237, 0.08);
  border-left: 4px solid transparent;
  border-radius: 2px;
  padding: 36px 28px;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.28), 0 10px 40px rgba(124, 58, 237, 0.16);
}

.stat-glass::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #7c3aed, #06b6d4);
}

.stat-glass::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}

.stat-glass:nth-child(2)::before {
  background: linear-gradient(180deg, #06b6d4, #ec4899);
}
.stat-glass:nth-child(3)::before {
  background: linear-gradient(180deg, #ec4899, #7c3aed);
}
.stat-glass:nth-child(4)::before {
  background: linear-gradient(180deg, #5b21b6, #06b6d4);
}

.stat-glass:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.36), 0 16px 56px rgba(124, 58, 237, 0.24);
  transform: translateY(-3px);
}

.stat-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
  color: #7c3aed;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.12), rgba(6, 182, 212, 0.1));
  border-radius: 2px;
  width: 48px;
  height: 48px;
  align-items: center;
  margin-left: auto;
  margin-right: auto;
}

.stat-value {
  font-family: var(--font-heading);
  font-size: 2.6em;
  font-weight: 800;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-family: var(--font-mono);
  font-size: 0.72em;
  color: var(--text-muted);
  letter-spacing: 1.5px;
  text-transform: uppercase;
  line-height: 1.5;
}

/* ============================================
   CASE STUDIES - Breach cards
   ============================================ */
.cases-section {
  position: relative;
  z-index: 1;
  padding: 56px 20px 88px;
  background: transparent;
}

.cases-inner {
  max-width: 920px;
  margin: 0 auto;
}

.region-label {
  display: flex;
  align-items: center;
  gap: 14px;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 4px;
  text-transform: uppercase;
  margin-bottom: 32px;
  margin-top: 16px;
  padding: 16px 4px;
  border-radius: 0;
  background: transparent;
  border: none;
  border-bottom: 2px solid #7c3aed;
}

.region-label span:last-child {
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.region-label-global {
  margin-top: 56px;
}

.region-icon {
  display: flex;
  color: #7c3aed;
}
.region-icon :deep(svg) {
  width: 22px;
  height: 22px;
}

.breach-card {
  display: flex;
  background: var(--bg-card);
  border: 1px solid rgba(124, 58, 237, 0.08);
  border-left: 4px solid transparent;
  margin-bottom: 28px;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.28), 0 10px 40px rgba(124, 58, 237, 0.16);
}

.breach-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #7c3aed, #ec4899);
}

.breach-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}

.breach-card:nth-child(odd)::before {
  background: linear-gradient(180deg, #7c3aed, #06b6d4);
}
.breach-card:nth-child(even)::before {
  background: linear-gradient(180deg, #5b21b6, #ec4899);
}

.breach-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.36), 0 16px 56px rgba(124, 58, 237, 0.24);
  transform: translateY(-3px);
}

.breach-severity {
  width: 6px;
  flex-shrink: 0;
}

.severity-critical {
  background: #ff1744;
}

.severity-high {
  background: #ff9100;
}

.breach-body {
  flex: 1;
  padding: 36px 32px;
}

.breach-header {
  margin-bottom: 28px;
}

.breach-title-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.breach-icon {
  display: flex;
  color: #7c3aed;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.12), rgba(6, 182, 212, 0.1));
  border-radius: 2px;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
}
.breach-icon :deep(svg) {
  width: 20px;
  height: 20px;
}

.breach-name {
  font-family: var(--font-heading);
  font-size: 1.3em;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.3px;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.breach-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.breach-tag {
  font-family: var(--font-mono);
  font-size: 0.68em;
  padding: 5px 14px;
  border-radius: 2px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-weight: 600;
}

.tag-date {
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
  border: 1px solid rgba(124, 58, 237, 0.25);
}

.tag-sector {
  background: rgba(6, 182, 212, 0.08);
  color: #06b6d4;
  border: 1px solid rgba(6, 182, 212, 0.25);
}

.tag-records {
  background: rgba(236, 72, 153, 0.08);
  color: #ec4899;
  border: 1px solid rgba(236, 72, 153, 0.25);
}

.breach-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.breach-detail-block {
  padding: 16px 18px;
  border-left: 3px solid var(--border-subtle);
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.02);
}

/* "What Happened" - red/danger tint */
.breach-detail-block:first-child {
  background: rgba(255, 23, 68, 0.04);
  border-left-color: #ff1744;
}

/* "Vendor Angle" - purple tint */
.breach-detail-block:nth-child(2) {
  background: rgba(124, 58, 237, 0.04);
  border-left-color: #7c3aed;
}

/* "How Enbarr Prevents" - cyan tint */
.breach-detail-block:nth-child(3) {
  background: rgba(6, 182, 212, 0.06);
  border-left-color: #06b6d4;
}

.breach-detail-label {
  font-family: var(--font-mono);
  font-size: 0.68em;
  font-weight: 800;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.breach-detail-text {
  font-size: 0.9em;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0;
}

.breach-vendor-text {
  color: #ec4899;
  font-weight: 500;
}

.breach-solution-text {
  color: #06b6d4;
  font-weight: 500;
}

/* ============================================
   PATTERN SECTION - Connected nodes
   ============================================ */
.pattern-section {
  position: relative;
  z-index: 1;
  padding: 88px 20px;
  background: transparent;
  border-top: none;
  border-bottom: none;
}

.pattern-inner {
  max-width: 920px;
  margin: 0 auto;
}

.pattern-flow {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  position: relative;
}

.pattern-node {
  text-align: center;
  padding: 28px 18px;
  position: relative;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid rgba(124, 58, 237, 0.08);
  border-left: 4px solid transparent;
  border-radius: 2px;
  margin: 0 6px;
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.28), 0 10px 40px rgba(124, 58, 237, 0.16);
}

.pattern-node::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #7c3aed, #06b6d4);
}

.pattern-node::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}

.pattern-node:nth-child(2)::before {
  background: linear-gradient(180deg, #ec4899, #7c3aed);
}
.pattern-node:nth-child(3)::before {
  background: linear-gradient(180deg, #06b6d4, #ec4899);
}
.pattern-node:nth-child(4)::before {
  background: linear-gradient(180deg, #5b21b6, #06b6d4);
}

.pattern-node:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.36), 0 16px 56px rgba(124, 58, 237, 0.24);
  transform: translateY(-3px);
}

.pattern-node-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.12), rgba(6, 182, 212, 0.1));
  border-radius: 2px;
  color: #7c3aed;
  position: relative;
}

.pattern-node-label {
  font-family: var(--font-heading);
  font-size: 0.92em;
  font-weight: 800;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 10px;
}

.pattern-node-desc {
  font-family: var(--font-mono);
  font-size: 0.7em;
  color: var(--text-muted);
  line-height: 1.65;
  letter-spacing: 0.3px;
}

.pattern-connector {
  position: absolute;
  top: 58px;
  right: -14px;
  width: 28px;
  display: flex;
  align-items: center;
  z-index: 2;
}

.connector-line {
  flex: 1;
  height: 2px;
  background: #7c3aed;
  border-radius: 0;
}

.connector-arrow {
  width: 0;
  height: 0;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 8px solid #7c3aed;
}

/* ============================================
   SOLUTION SECTION
   ============================================ */
.solution-section {
  position: relative;
  z-index: 1;
  padding: 88px 20px;
  background: transparent;
}

.solution-inner {
  max-width: 1100px;
  margin: 0 auto;
}

.solution-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 36px;
}

.solution-card {
  background: var(--bg-card);
  border: 1px solid rgba(124, 58, 237, 0.08);
  border-left: 4px solid transparent;
  border-radius: 2px;
  padding: 40px 30px;
  position: relative;
  overflow: hidden;
  transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.28), 0 10px 40px rgba(124, 58, 237, 0.16);
}

.solution-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #06b6d4, #7c3aed);
}

.solution-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}

.solution-card:nth-child(2)::before {
  background: linear-gradient(180deg, #7c3aed, #ec4899);
}
.solution-card:nth-child(3)::before {
  background: linear-gradient(180deg, #ec4899, #06b6d4);
}
.solution-card:nth-child(4)::before {
  background: linear-gradient(180deg, #5b21b6, #06b6d4);
}
.solution-card:nth-child(5)::before {
  background: linear-gradient(180deg, #06b6d4, #ec4899);
}

.solution-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.36), 0 16px 56px rgba(124, 58, 237, 0.24);
  transform: translateY(-3px);
}

.solution-card-icon {
  display: flex;
  color: #7c3aed;
  margin-bottom: 22px;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.12), rgba(6, 182, 212, 0.1));
  border-radius: 2px;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
}

.solution-card-title {
  font-family: var(--font-heading);
  font-size: 1.1em;
  font-weight: 800;
  margin: 0 0 14px;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.solution-card-text {
  font-size: 0.88em;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0;
}

/* ============================================
   CTA
   ============================================ */
.cta-section {
  position: relative;
  z-index: 1;
  padding: 48px 20px 108px;
  background: transparent;
}

.cta-inner {
  max-width: 740px;
  margin: 0 auto;
}

.cta-card {
  background: var(--bg-card);
  border: 1px solid rgba(124, 58, 237, 0.08);
  border-left: 4px solid transparent;
  border-radius: 2px;
  padding: 64px 52px;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.28), 0 10px 40px rgba(124, 58, 237, 0.16);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

.cta-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #7c3aed, #06b6d4);
}

.cta-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.36), 0 16px 56px rgba(124, 58, 237, 0.24);
  transform: translateY(-3px);
}

.cta-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
}

.cta-title {
  font-family: var(--font-heading);
  font-size: 2.2em;
  font-weight: 800;
  margin: 0 0 20px;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 30%, #6366f1 60%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cta-text {
  font-size: 1em;
  color: var(--text-secondary);
  line-height: 1.85;
  margin: 0 0 36px;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 42px;
  font-family: var(--font-mono);
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  text-decoration: none;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #6366f1, #06b6d4);
  border: none;
  border-radius: 2px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
}

.cta-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(124, 58, 237, 0.4), 0 4px 16px rgba(124, 58, 237, 0.2);
}

.cta-arrow {
  font-size: 1.3em;
  transition: transform 0.3s ease;
}

.cta-button:hover .cta-arrow {
  transform: translateX(5px);
}

/* ============================================
   DARK MODE
   ============================================ */
:root[data-theme="dark"] .hero {
  background: transparent;
}

:root[data-theme="dark"] .hero-card {
  background: rgba(20, 15, 40, 0.85);
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.35), 0 10px 40px rgba(124, 58, 237, 0.2);
  border-color: rgba(124, 58, 237, 0.15);
}

:root[data-theme="dark"] .hero-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.45), 0 16px 56px rgba(124, 58, 237, 0.3);
}

:root[data-theme="dark"] .hero-title {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .hero-subtitle {
  color: rgba(255, 255, 255, 0.7);
}

:root[data-theme="dark"] .section-title {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .section-badge {
  background: linear-gradient(135deg, #7c3aed, #6366f1, #06b6d4);
  color: #fff;
  border: none;
}

:root[data-theme="dark"] .stats-section {
  background: transparent;
}

:root[data-theme="dark"] .cta-section {
  background: transparent;
}

:root[data-theme="dark"] .cta-card {
  background: rgba(20, 15, 40, 0.85);
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.35), 0 10px 40px rgba(124, 58, 237, 0.2);
  border-color: rgba(124, 58, 237, 0.15);
}

:root[data-theme="dark"] .cta-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.45), 0 16px 56px rgba(124, 58, 237, 0.3);
}

:root[data-theme="dark"] .cta-title {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .cta-text {
  color: rgba(255, 255, 255, 0.7);
}

:root[data-theme="dark"] .region-label {
  border-bottom-color: #a78bfa;
}

:root[data-theme="dark"] .region-label span:last-child {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .pattern-node-icon {
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.15), rgba(34, 211, 238, 0.12));
  color: #a78bfa;
}

:root[data-theme="dark"] .cases-section {
  background: transparent;
}

:root[data-theme="dark"] .breach-card {
  background: rgba(20, 15, 40, 0.85);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.35), 0 10px 40px rgba(124, 58, 237, 0.2);
}

:root[data-theme="dark"] .breach-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.45), 0 16px 56px rgba(124, 58, 237, 0.3);
}

:root[data-theme="dark"] .breach-name {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .breach-detail-block:first-child {
  background: rgba(255, 23, 68, 0.08);
}

:root[data-theme="dark"] .breach-detail-block:nth-child(2) {
  background: rgba(124, 58, 237, 0.06);
}

:root[data-theme="dark"] .breach-detail-block:nth-child(3) {
  background: rgba(6, 182, 212, 0.08);
}

:root[data-theme="dark"] .pattern-section {
  background: transparent;
  border-top: none;
  border-bottom: none;
}

:root[data-theme="dark"] .pattern-node {
  background: rgba(20, 15, 40, 0.85);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.35), 0 10px 40px rgba(124, 58, 237, 0.2);
}

:root[data-theme="dark"] .pattern-node:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.45), 0 16px 56px rgba(124, 58, 237, 0.3);
}

:root[data-theme="dark"] .pattern-node-label {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .solution-section {
  background: transparent;
}

:root[data-theme="dark"] .solution-card {
  background: rgba(20, 15, 40, 0.85);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.35), 0 10px 40px rgba(124, 58, 237, 0.2);
}

:root[data-theme="dark"] .solution-card:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.45), 0 16px 56px rgba(124, 58, 237, 0.3);
}

:root[data-theme="dark"] .solution-card-title {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .stat-glass {
  background: rgba(20, 15, 40, 0.85);
  box-shadow: 6px 6px 0 rgba(124, 58, 237, 0.35), 0 10px 40px rgba(124, 58, 237, 0.2);
  border-color: rgba(124, 58, 237, 0.15);
}

:root[data-theme="dark"] .stat-glass:hover {
  box-shadow: 8px 8px 0 rgba(124, 58, 237, 0.45), 0 16px 56px rgba(124, 58, 237, 0.3);
}

:root[data-theme="dark"] .stat-value {
  background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #22d3ee 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

:root[data-theme="dark"] .stat-icon {
  color: #a78bfa;
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.15), rgba(34, 211, 238, 0.12));
}

:root[data-theme="dark"] .breach-icon {
  color: #a78bfa;
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.15), rgba(34, 211, 238, 0.12));
}

:root[data-theme="dark"] .region-icon {
  color: #a78bfa;
}

:root[data-theme="dark"] .tag-date {
  background: rgba(167, 139, 250, 0.12);
  color: #a78bfa;
  border-color: rgba(167, 139, 250, 0.25);
}

:root[data-theme="dark"] .tag-sector {
  background: rgba(34, 211, 238, 0.12);
  color: #22d3ee;
  border-color: rgba(34, 211, 238, 0.25);
}

:root[data-theme="dark"] .tag-records {
  background: rgba(236, 72, 153, 0.12);
  color: #f472b6;
  border-color: rgba(236, 72, 153, 0.25);
}

:root[data-theme="dark"] .breach-vendor-text {
  color: #f472b6;
}

:root[data-theme="dark"] .breach-solution-text {
  color: #22d3ee;
}

:root[data-theme="dark"] .solution-card-icon {
  color: #a78bfa;
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.15), rgba(34, 211, 238, 0.12));
}

:root[data-theme="dark"] .connector-line {
  background: #a78bfa;
}

:root[data-theme="dark"] .connector-arrow {
  border-left-color: #a78bfa;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .solution-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .pattern-flow {
    grid-template-columns: repeat(2, 1fr);
    gap: 36px;
  }
  .pattern-connector {
    display: none;
  }
}

@media (max-width: 750px) {
  .hero {
    padding: 80px 16px 40px;
  }
  .hero-card {
    padding: 40px 24px;
  }
  .hero-title {
    font-size: 2.1em;
  }
  .section-title {
    font-size: 1.7em;
  }
  .stats-grid {
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .stat-glass {
    padding: 28px 18px;
  }
  .stat-value {
    font-size: 1.8em;
  }
  .breach-card {
    flex-direction: column;
  }
  .breach-severity {
    width: 100%;
    height: 5px;
  }
  .breach-body {
    padding: 28px 22px;
  }
  .breach-meta {
    flex-wrap: wrap;
  }
  .solution-grid {
    grid-template-columns: 1fr;
  }
  .pattern-flow {
    grid-template-columns: 1fr 1fr;
  }
  .cta-card {
    padding: 40px 24px;
  }
  .cta-title {
    font-size: 1.6em;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  .pattern-flow {
    grid-template-columns: 1fr;
  }
}
</style>
